import { useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { fetchDashboard } from './api';
import {
  dashboardFilterAtom,
  dashboardFilterChangeTrackerAtom,
} from './filterAtom';
import { dashboardAtom } from './atoms';
import { ServerDashboardDashboard } from './dashboardType';

export const usePagination = () => {
  const filters = useAtomValue(dashboardFilterAtom);
  const filterChangeTracker = useAtomValue(dashboardFilterChangeTrackerAtom);
  const setFilters = useSetAtom(dashboardFilterAtom);
  const setDashboardData = useSetAtom(dashboardAtom);

  const { data, isLoading, isPlaceholderData, isError, error } =
    useQuery<ServerDashboardDashboard>({
      placeholderData: keepPreviousData,
      queryFn: () => fetchDashboard(filters),
      queryKey: ['dashboard', filters],
      refetchInterval: 3000,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (data) {
      setDashboardData(data);
    }
  }, [data, setDashboardData]);

  // Jump back to page 1 whenever the actual filter criteria change so the
  // user never lands on a page that no longer has any matching results.
  useEffect(() => {
    setFilters((previousFilters) => ({ ...previousFilters, activePage: 1 }));
  }, [filterChangeTracker, setFilters]);

  const goToPage = (pageNumber: number) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      activePage: pageNumber,
    }));
  };

  const setItemsPerPage = (newItemsPerPage: number) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      activePage: 1,
      itemsPerPage: newItemsPerPage,
    }));
  };

  const totalJobs = data?.total || 0;
  const totalPages = Math.ceil(totalJobs / filters.itemsPerPage);

  return {
    currentPage: filters.activePage,
    error,
    goToPage,
    isError,
    isFetchingNewPage: isPlaceholderData,
    isLoading,
    itemsPerPage: filters.itemsPerPage,
    jobs: data?.jobs || [],
    setItemsPerPage,
    totalJobs,
    totalPages,
  };
};
